//@flow
import React from 'react';
import {Autocomplete} from '@material-ui/lab';
import {makeStyles, Typography} from '@material-ui/core';

import {colors} from '../../assets/styles/colors';
import {useCategories} from '../../modules/categories/utils';
import {useStorePromotions} from '../../modules/marketing/promotions/utils';
import {useStoreDomain} from '../../modules/store/utils';
import {useSearchMinimalProducts} from '../../modules/products/utils/hooks';
import {getImageUrl} from '../../utils/image_provider';
import {GENERIC_STOREFRONT_PAGES} from '../../modules/home-pages/components/Sections/BannerGroup/BannerForm/constants';
import {debounce} from '../../utils/functions';

import type {BannerButtonEntityType} from '../../entities';
import CustomTextField from '../CustomTextField/CustomTextField';

const useStyles = makeStyles(theme => ({
  autocompleteContainer: {
    position: 'relative',
    width: '100%',
  },
  autocompleteLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    height: '2.5rem',
    padding: '0.25rem',
    width: '100%',
    overflow: 'hidden',
  },
  autocompleteThumbnail: {
    position: 'relative',
    height: '100%',
    borderRadius: '0.25rem',
    aspectRatio: '1/1',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  autocompleteData: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    width: '90%',
  },
  autocompleteLabelName: {
    fontWeight: '500',
    fontSize: '0.75rem',
  },
  autocompleteLabelUrl: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.75rem',
    color: colors.primary,
    maxWidth: '100%',
  },
}));

type UrlInputOption = {
  name: string,
  id: string,
  thumbnail?: string,
  url: string,
  typeLabel:
    | 'Categorías'
    | 'Promociones'
    | 'Productos'
    | 'Páginas genéricas'
    | 'URL',
  type: BannerButtonEntityType,
  disabled?: boolean,
};

type Props = {
  onSelectUrl: (url: UrlInputOption) => void,
  helperText?: string,
  error?: boolean,
  freeSolo?: boolean,
  defaultValue?: string,
};

export default function InternalPageRouteAutocomplete(props: Props) {
  const {onSelectUrl, helperText, error, freeSolo, defaultValue} = props;

  const classes = useStyles();

  const categories = useCategories();
  const promotions = useStorePromotions();
  const {domain} = useStoreDomain();

  const {loading, products, searchProductsByName} = useSearchMinimalProducts({
    initialList: true,
  });

  const categoryOption: UrlInputOption[] = categories.map(category => ({
    name: category.name,
    id: category.id,
    thumbnail: getImageUrl(category.portraitImageId),
    url: `https://${domain && domain.domainName}/category/${category.id}`,
    typeLabel: 'Categorías',
    type: 'CATEGORY',
    disabled: !category.enabled,
  }));

  const promotionOption: UrlInputOption[] = promotions.map(promotion => ({
    name: promotion.name,
    id: `${promotion.slug}`,
    thumbnail: getImageUrl(promotion.promotionImageId),
    url: `https://${domain && domain.domainName}/promo/${promotion.slug}`,
    typeLabel: 'Promociones',
    type: 'PROMOTION',
  }));

  const productOption: UrlInputOption[] = products.map(product => ({
    name: product.name,
    id: product.id,
    thumbnail: getImageUrl(product.mainImageId),
    url: `https://${domain && domain.domainName}/product/${product.id}`,
    typeLabel: 'Productos',
    type: 'PRODUCT',
  }));

  const genericPagesOption: UrlInputOption[] = GENERIC_STOREFRONT_PAGES.map(
    page => ({
      name: page.label,
      id: `https://${domain && domain.domainName}${page.path}`,
      url: `https://${domain && domain.domainName}${page.path}`,
      typeLabel: 'Páginas genéricas',
      type: 'URL',
    })
  );

  const handleInputChange = debounce((value: string) => {
    if (!value.trim() && value.length >= 3) return;

    searchProductsByName(value);
  }, 750);

  return (
    <div className={classes.autocompleteContainer}>
      <Autocomplete
        disablePortal
        options={[
          ...productOption,
          ...genericPagesOption,
          ...categoryOption,
          ...promotionOption,
        ]}
        defaultValue={defaultValue}
        fullWidth
        loading={loading}
        autoComplete
        filterOptions={(options, state) => {
          const {inputValue} = state;

          const resultOptions = options.filter(option =>
            option.name.toLowerCase().includes(inputValue.toLowerCase())
          );
          return resultOptions;
        }}
        clearOnBlur={!freeSolo}
        freeSolo={freeSolo}
        getOptionLabel={option => {
          if (typeof option === 'string') return option;
          return option.url;
        }}
        groupBy={option => option.typeLabel}
        onInput={event => {
          const value = event.target.value;
          handleInputChange(value);
          if (freeSolo) {
            onSelectUrl({
              name: value,
              id: value,
              url: value,
              typeLabel: 'URL',
              type: 'URL',
            });
          }
        }}
        onChange={(event, value) => {
          if (!value) return;
          if (typeof value === 'string') return;
          onSelectUrl(value);
        }}
        getOptionDisabled={option => option.disabled}
        renderOption={(option, state) => {
          const {selected} = state;

          return (
            <div className={classes.autocompleteLabel} data-selected={selected}>
              {option.thumbnail && (
                <picture className={classes.autocompleteThumbnail}>
                  <img src={option.thumbnail} alt="" />
                </picture>
              )}
              <div className={classes.autocompleteData}>
                <Typography className={classes.autocompleteLabelName}>
                  {option.name}
                </Typography>
                <Typography className={classes.autocompleteLabelUrl}>
                  {option.url}
                </Typography>
              </div>
            </div>
          );
        }}
        renderInput={params => (
          <CustomTextField
            {...params}
            label=""
            placeholder="Selecciona una URL de redirección"
            type="url"
            fullWidth
            helperText={helperText}
            error={error}
          />
        )}
      />
    </div>
  );
}
